
/* eslint-disable brace-style */

import { inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';

import { JwtHelperService } from '@auth0/angular-jwt';
import { Store } from '@ngrx/store';

import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { injectConfigs } from '../config/utils';
import { SharedAction } from '../states/shared/shared.action';
import { checkAndExtractRoleAgency } from './checkAndExtractRoleAgency';
import { checkPosDecent } from './checkPos';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private agency = injectConfigs().endPoints.AGENCY_ROLE_NAME;
  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  private canAccessAgencySubject$ = new BehaviorSubject<boolean>(false);
  public canAccessAgency$ = this.canAccessAgencySubject$.asObservable();

  private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  private urlToRedirectSubject$ = new BehaviorSubject<string>('');
  public urlToRedirect$ = this.urlToRedirectSubject$.asObservable();

  private username: BehaviorSubject<string> = new BehaviorSubject<string>("");
  public username$: Observable<string> = this.username.asObservable();

  setUsername(name: string) {
    this.username.next(name.toLowerCase());
  }

  private _store = inject(Store);

  private _jwt = inject(JwtHelperService);
  public extractUsername(): string {
    return this._jwt.decodeToken(this.oauthService.getAccessToken())
      .preferred_username;
  }
  public canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.isDoneLoading$,
    this.canAccessAgencySubject$,
  ]).pipe(map((values) => values.every((b) => b)));

  public canActivateAgencyProtectedRoutes$: Observable<boolean> = combineLatest(
    [this.isAuthenticated$, this.isDoneLoading$, this.canAccessAgencySubject$]
  ).pipe(map((values) => values.every((b) => b)));

  private navigateToLoginPage() {
    this.router.navigateByUrl('/should-login');
  }

  constructor(private oauthService: OAuthService, private router: Router) {
    this.oauthService.events.subscribe((event) => {
      if (event instanceof OAuthErrorEvent) {
        console.error('OAuthErrorEvent Object:', event);
      } else {
        console.warn('OAuthEvent Object:', event);
      }
    });
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url !== '/force-logout') {
          this.urlToRedirectSubject$.next(event.url);
        }
      }
    });
    window.addEventListener('storage', (event) => {
      if (event.key !== 'access_token' && event.key !== null) {
        return;
      }

      console.warn(
        'Noticed changes to access_token (most likely from another tab), updating isAuthenticated'
      );
      this.isAuthenticatedSubject$.next(
        this.oauthService.hasValidAccessToken()
      );

      if (!this.oauthService.hasValidAccessToken()) {
        this.navigateToLoginPage();
      }
    });

    this.oauthService.events.subscribe((_) => {
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
      this.setUsername(this.oauthService.getIdentityClaims()['name']);

      if (!!this.oauthService.getIdentityClaims() && _.type === 'user_profile_loaded') {
        const roles: string | string[] | null | undefined = this.oauthService.getIdentityClaims()['role'];
        const res = (Array.isArray(roles) && !!checkAndExtractRoleAgency(roles)) || checkPosDecent(this.oauthService.getIdentityClaims()?.['BranchPOSManager']);
        this.canAccessAgencySubject$.next(res);

        if (res) {

          this._store.dispatch(
            SharedAction.setAgency({ agency: checkAndExtractRoleAgency(roles) })
          );
          this.router.navigateByUrl(this.urlToRedirectSubject$.value);
        }

        // check and get position
        let countClaims = 0;
        if (this.oauthService.getIdentityClaims()['TenantManager']) {
          this._store.dispatch(
            SharedAction.setPosition({
              position: this.oauthService.getIdentityClaims()['TenantManager'],
            })
          );
          countClaims = countClaims + 1;
        }
        if (countClaims > 1) {
          router.navigate(['/force-claim-change']);
        }
        const claims = this.oauthService.getIdentityClaims();
        let finalPosition = '';

        if (claims && claims['TenantManager']) {
          finalPosition = claims['TenantManager'];
        }
        else if (claims && claims['BranchManager']) {
          const backendRole = claims['BranchManager'];

          switch (backendRole) {
            case 'Warehouse':
            case 'Region':
              finalPosition = 'RequireWarehouseAccountRole';
              break;
            case 'Manager':
              finalPosition = 'RequireTenantManagerRole';
              break;
            case 'SaleAssistant':
              finalPosition = 'RequireTenantSaleAssistantRole';
              break;
            case 'Marketing':
              finalPosition = 'RequireTenantMarketingRole';
              break;
          }
        }

        if (finalPosition) {
          this._store.dispatch(
            SharedAction.setPosition({
              position: finalPosition,
            })
          );
        }
      }
    });

    this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

    this.oauthService.events
      .pipe(filter((e) => ['token_received'].includes(e.type)))
      .subscribe(() => this.oauthService.loadUserProfile());

    this.oauthService.events
      .pipe(
        filter((e) => ['session_terminated', 'session_error'].includes(e.type))
      )
      .subscribe(() => this.navigateToLoginPage());

    this.oauthService.setupAutomaticSilentRefresh();
  }

  public runInitialLoginSequence() {
    if (location.hash) {
      console.table(
        location.hash
          .substr(1)
          .split('&')
          .map((kvp) => kvp.split('='))
      );
    }

    return this.oauthService
      .loadDiscoveryDocument()
      .then(() => {
        this.oauthService.tryLoginImplicitFlow().then(() => {
          if (!this.oauthService.hasValidAccessToken()) {
            return this.oauthService.tryLoginCodeFlow().then(() => {
              this.isDoneLoadingSubject$.next(true);

              return Promise.resolve();
            });
          } else {
            return this.oauthService.loadUserProfile().then(() => {
              this.isDoneLoadingSubject$.next(true);

              return Promise.resolve();
            });
          }
        });
      })
      .then(() => {
        if (
          this.oauthService.state &&
          this.oauthService.state !== 'undefined' &&
          this.oauthService.state !== 'null'
        ) {
          let stateUrl = this.oauthService.state;
          if (!stateUrl.startsWith('/')) {
            stateUrl = decodeURIComponent(stateUrl);
          }

          this.router.navigateByUrl(stateUrl);
          return Promise.resolve();
        }
        return Promise.resolve();
      })
      .catch(() => this.isDoneLoadingSubject$.next(true));
  }

  public login(targetUrl?: string) {
    this.oauthService.initLoginFlow(targetUrl || this.router.url);
  }

  public logout() {
    this.oauthService.logOut();
  }
  public refresh() {
    this.oauthService.silentRefresh();
  }
  public hasValidToken() {
    return this.oauthService.hasValidAccessToken();
  }

  public get accessToken() {
    return this.oauthService.getAccessToken();
  }
  public get refreshToken() {
    return this.oauthService.getRefreshToken();
  }
  public get identityClaims() {
    return this.oauthService.getIdentityClaims();
  }
  public get idToken() {
    return this.oauthService.getIdToken();
  }
  public get logoutUrl() {
    return this.oauthService.logoutUrl;
  }
}
